import { useEffect, useState, useMemo } from "react";
import { colDegrees, colNumber, colText, DataGrid } from "./common/DataGrid";
import {
    OrbitalPass,
    OrbitalPassStep,
    WorkerRequest,
    WorkerRequestStart,
    WorkerRequestType,
    WorkerResponse,
    WorkerResponseError,
    WorkerResponsePass,
    WorkerResponseProgress,
    WorkerResponseType
} from "./OrbitFeasibilityStudyWebWorkerTypes";
import { Form, ProgressBar, Tab, Tabs } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Instant, LocalDate, ZoneOffset } from "@js-joda/core";
import { geocodeLocation } from "./api/Mapbox";
import { ColDef, GetRowIdParams } from "ag-grid-community";
import { showBrowserDate, showBrowserTimeSec } from "./api/Date";
import { Button } from "./components/Button";
import { getRowStyle, useSimulationWorker } from "./OrbitFeasibilityStudyPageUtil";
import { X } from "react-bootstrap-icons";
import { LatLng } from "./common/Geography";
import { computePasses } from "./common/Orbital";

interface OrbitForm {
    target:    string;
    startDate: string;
    endDate:   string;
}

export function OrbitFeasibilityStudyPage() {
    interface Status {
        status: string;
        value: number;
    }

    //state
    type Tabs = "raw" | "passes";
    const [ tab,    setTab    ] = useState<Tabs>();
    const [ status, setStatus ] = useState<Status>();

    //just some convenient values for now
    //eventually derive them from the muni
    const defaultValues = {
        target:    "Philadelphia",
        startDate: showBrowserDate( LocalDate.now() ),
        endDate:   showBrowserDate( LocalDate.now().plusDays(5) )
    };
    const { handleSubmit, register } = useForm<OrbitForm>( { defaultValues: defaultValues } );        

    //worker section
    const { post, recv } = useSimulationWorker<WorkerRequest, WorkerResponse>();
    const [ orbitSteps, setOrbitSteps ] = useState<OrbitalPassStep[]>( [] );
    
    useEffect( () => {
        recv( WorkerResponseType.PassFound, ( msg: WorkerResponsePass ) => {
            setOrbitSteps( prev => [ ...prev, ...msg.payload ] );
        } );
        recv( WorkerResponseType.Progress, ( msg: WorkerResponseProgress ) => {
            setStatus( { status: msg.status, value: msg.value } );
        } );
        recv( WorkerResponseType.Error, ( msg: WorkerResponseError ) => {
            console.error( 'Worker Error:', msg );
        } );
    }, [recv] );
    //end worker section

    function showTime( p: { value: number; } ) {
        return showBrowserTimeSec( Instant.ofEpochMilli( p.value ).atZone( ZoneOffset.UTC ).toLocalTime() );
    }

    function showDate( p: { value: number; } ) {
        return showBrowserDate( Instant.ofEpochMilli( p.value ).atZone( ZoneOffset.UTC ).toLocalDate() );
    }

    function goodness( pass: OrbitalPass ): number {
        //returns a number between 0-255
        let res = 0;

        return res;
    }

    const colDefs = useMemo( (): ColDef<OrbitalPassStep>[] => [
        { ...colText     (  "satelliteName" ),  headerName: "Satellite"                                },
        { field:            "date",             headerName: "Date",           valueFormatter: showDate },
        { field:            "tick",             headerName: "Time",           valueFormatter: showTime },
        { field:            "epoch",            headerName: "Epoch",          valueFormatter: showDate },
        { ...colNumber    ( "altitude"  ),      headerName: "Alt.",           valueFormatter: p => p.value.toFixed(1) },
        { ...colNumber    ( "slantRange" ),     headerName: "Slant Range",    valueFormatter: p => p.value.toFixed(1) },
        { ...colNumber    ( "groundRange" ),    headerName: "Ground Range",   valueFormatter: p => p.value.toFixed(1) },
        { ...colDegrees   ( "offNadir"  ),      headerName: "Off-Nadir",      valueFormatter: p => p.value.toFixed(1), },
        { ...colDegrees   ( "sunElev"   ),      headerName: "Sun Elevation",  valueFormatter: p => p.value.toFixed(1), },
        { ...colNumber    ( "cloudCover" ),     headerName: "Cloud Cover",    valueFormatter: p => p.value.toFixed(0), },
    ], [] );

    const passesColDefs = useMemo( (): ColDef<OrbitalPass>[] => [
        { ...colText    ( "satelliteName"                 ), headerName: "Satellite",         },
        { field: "date",      valueFormatter:      showDate, headerName: "Date",              },
        { field: "earlyTime", valueFormatter:      showTime, headerName: "Early",        },
        { field: "midTime",   valueFormatter:      showTime, headerName: "Apex",         },
        { field: "lastTime",  valueFormatter:      showTime, headerName: "Last",         },
        { field: "samples",   type: "rightAligned",          headerName: "Samples",           },
     // { ...colNumber    ( "minAlt"       ), valueFormatter: p => p.value.toFixed(0), headerName: "Min Altitude", },
        { ...colNumber    ( "apexAlt"      ), valueFormatter: p => p.value.toFixed(0), headerName: "Apex Alt.", },
     // { ...colNumber    ( "maxAlt"       ), valueFormatter: p => p.value.toFixed(0), headerName: "Max Altitude", },
        { ...colDegrees   ( "sunElev"      ), headerName: "Sun Elev.",  valueFormatter: p => p.value.toFixed(1), },
        { ...colDegrees   ( "firstOffNadir"  ), headerName: "First ONA",  valueFormatter: p => p.value.toFixed(1), },
        { ...colDegrees   ( "apexOffNadir" ), headerName: "Apex ONA", valueFormatter: p => p.value.toFixed(1), },
        { ...colDegrees   ( "lastOffNadir"  ), headerName: "Last ONA ", valueFormatter: p => p.value.toFixed(1), },
        { ...colNumber    ( "cloudCover"   ), headerName: "Cloud Cover ", valueFormatter: p => p.value.toFixed(0), }
    ], [] );

    const orbitalPasses = useMemo( () => {        
        //we'll only compute when it's at a 'completion' stage
        if( status?.value !== 1.0 ) { return []; }        
        return computePasses( orbitSteps );
    }, [orbitSteps, status] );

    const onSubmit = async ( data: OrbitForm ) => {
        setOrbitSteps( [] ); //reset it
        const result = await geocodeLocation( data.target );
        if( result ) {
            console.log( `Found: ${data.target} at ${result.lat}, ${result.lng}` );
            const startTmp = LocalDate.parse( data.startDate );
            const endTmp   = LocalDate.parse( data.endDate );
            const newLocal: WorkerRequestStart = {
                type:        WorkerRequestType.Start,
                target:      [result.lat, result.lng] as LatLng,
                startDate:   startTmp.toString(),
                endDate:     endTmp.toString(),
                maxOffNadir: 20,
                minSunElev:  30,
            };
            post( newLocal );
        }
        else {
            console.warn( `Location not found: ${data.target}` );
        }
    }

    /**
 * Converts an array of objects to a CSV string and triggers a browser download.
 * Columns are inferred from the keys of the first row unless `columns` is provided.
 */
function downloadCsv<T extends Record<string, any>>(
    rows:     T[],
    filename: string,
    columns?: ( keyof T )[]
) {
    if( rows.length === 0 ) {
        console.warn( "downloadCsv: no rows to export" );
        return;
    }

    const cols = columns ?? ( Object.keys( rows[0] ) as ( keyof T )[] );
    const header = cols.map( c => escapeCell( String( c ) ) ).join( "," );
    const body   = rows.map( row =>
        cols.map( c => escapeCell( formatCell( row[c] ) ) ).join( "," )
    ).join( "\n" );

    const csv  = `${header}\n${body}`;
    const blob = new Blob( [csv], { type: "text/csv;charset=utf-8;" } );
    const url  = URL.createObjectURL( blob );
    const a    = document.createElement( "a" );
    a.href     = url;
    a.download = filename;
    document.body.appendChild( a );
    a.click();
    document.body.removeChild( a );
    URL.revokeObjectURL( url );
}

function formatCell( val: unknown ): string {
    if( val == null )            return "";
    if( typeof val === "number" ) return Number.isFinite( val ) ? String( val ) : "";
    if( val instanceof Date )    return val.toISOString();
    return String( val );
}

function escapeCell( s: string ): string {
    if( /[",\n\r]/.test( s ) ) {
        return `"${s.replace( /"/g, '""' )}"`;
    }
    return s;
}

    return (
        <div className="h-100">
            <div id="capture-list-page">
                <div id="capture-list" className="bg-body-tertiary border border-end-0">
                    <div className="d-flex flex-column gap-2 border-bottom p-2">
                        <Form onSubmit={handleSubmit( onSubmit )} className="d-flex flex-column gap-2">
                            <Form.Control
                                type="text"
                                {...register( "target", { required: true } )}
                                placeholder="City, Town, Suburb"
                            />
                            <Form.Control
                                type="date"
                                {...register( "startDate", { required: true } )}
                                placeholder="Start Date"
                            />
                            <Form.Control
                                type="date"
                                {...register( "endDate", { required: true } )}
                                placeholder="End Date"
                            />
                            <Button type="submit">
                                Start Simulation
                            </Button>
                            <Button onClick={ () => downloadCsv( orbitalPasses, "passes.csv" )}>
                                Download
                            </Button>
                        </Form>
                        <div className="pt-2">
                            {status?.status}
                        </div>
                        <div>
                            <ProgressBar
                                // striped
                                style={{ height: "36px" }}
                                // className="border border-primary"
                                min={0}
                                max={1.0}
                                now={status?.value} />
                        </div>
                        <div>
                            Total Samples: {orbitSteps.length}
                        </div>
                    </div>
                </div>
                <div className="image-viewer h-100 overflow-hidden position-relative">
                    <div style={{ width: "100%", height: "100%", display: "grid", gridTemplateRows: "auto 1fr" }}>
                        <Tabs defaultActiveKey={"passes"}
                              activeKey={tab}
                              onSelect={ x => setTab( x as any ) }
                              variant="underline"
                              justify={true}>
                                <Tab eventKey={"passes"} title={"Passes"} className="h-100">
                                    <DataGrid
                                        className="h-100"
                                        columnDefs={passesColDefs}
                                        rowData={orbitalPasses}
                                    />
                                </Tab>
                                <Tab eventKey={"raw"} title="Raw Samples" className="h-100">
                                    <DataGrid
                                        className="h-100"
                                        getRowId={( p: GetRowIdParams<OrbitalPassStep> ) => `${p.data.satelliteName}-${p.data.tick.toString()}`}
                                        columnDefs={colDefs}
                                        rowData={orbitSteps}
                                        getRowStyle={getRowStyle}
                                    />
                                </Tab>                                
                        </Tabs>                        
                    </div>
                </div>
            </div>
        </div>
    );
}